import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { take } from 'rxjs/operators';

import {
  TrialInsightsResponseGeneric,
  TrialInsightsSiteCostInputType,
  TrialInsightsSiteCostResponseType,
  TrialInsightsState,
} from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsStore } from '@models/trial-insights/trial-insights.store';

import { GqlService, SortOrder } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { MainQuery } from '@shared/store/main/main.query';

type LoadingState = 'completed' | RequestType;
type RequestInput = TrialInsightsSiteCostInputType;
type RequestType = 'initial' | 'remaining';
type Response = TrialInsightsResponseGeneric<ResponseType>[];
type ResponseType = TrialInsightsSiteCostResponseType;
type State = Partial<TrialInsightsState['siteCost']>;

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsClinicalSiteCostStoreService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private store = inject(TrialInsightsStore);

  getSiteCostSummary$(): Observable<unknown> {
    return this.mainQuery
      .select('trialKey')
      .pipe(
        this.updateLoadingState('initial'),
        this.request('initial'),
        this.processResponse(),
        this.updateLoadingState('remaining'),
        this.request('remaining'),
        this.processResponse(),
        this.updateLoadingState('completed')
      );
  }

  loadingState(type: LoadingState): State {
    const loadingState: State = {};

    if (type === 'initial') {
      loadingState.isLoading = true;
      loadingState.isLoadingRemaining = true;
      loadingState.data = null;
    } else if (type === 'remaining') {
      loadingState.isLoading = false;
    } else {
      loadingState.isLoadingRemaining = false;
    }

    return loadingState;
  }

  processResponse() {
    const processFn = (sourceValue: Response, subscriber: Subscriber<unknown>) => {
      if (!sourceValue.length) {
        return subscriber.next(sourceValue);
      }

      const stateUpdates = sourceValue.reduce((updates, update) => {
        if (!update.data) {
          return updates;
        }

        updates.push(update.data);

        return updates;
      }, [] as ResponseType[]);

      this.store.update((state) => {
        const currentData = state.siteCost.data || [];

        return {
          ...state,
          siteCost: {
            ...state.siteCost,
            data: [...currentData, ...stateUpdates],
          },
        };
      });

      subscriber.next(sourceValue);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  request(type: RequestType) {
    const processFn = (sourceValue: unknown, subscriber: Subscriber<Response>) => {
      const inputs = this.requestInput(type);
      const input$ = inputs.map((input) => this.gqlService.getSiteCostSummary$(input));

      forkJoin(input$)
        .pipe(take(1))
        .subscribe((results) => {
          subscriber.next(results);
        });
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  requestInput(type: RequestType): RequestInput[] {
    const request = [
      {
        site_cost_order: SortOrder.DESC,
        sites_lag_order: SortOrder.ASC,
      },
    ];

    if (type === 'remaining') {
      request[0].site_cost_order = SortOrder.ASC;
      request[0].sites_lag_order = SortOrder.DESC;
    }

    return request;
  }

  updateLoadingState(type: LoadingState) {
    const updates = this.loadingState(type);

    const processFn = (sourceValue: unknown, subscriber: Subscriber<unknown>) => {
      this.store.update((state) => {
        return {
          ...state,
          siteCost: {
            ...state.siteCost,
            ...updates,
          },
        };
      });

      subscriber.next(updates);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }
}
